import React, { useState, useEffect } from 'react';
import './DocumentsUtilesPage.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toSentenceCase } from '../utils/textUtils';
import fournitureService from '../services/fournitureService';
import { defaultFournitures } from '../data/defaultFournitures';
import { defaultSchedules } from '../data/defaultSchedules';
import useSEO from '../hooks/useSEO';

// Vector SVG Icons
const IconDocument = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
    <polyline points="10 9 9 9 8 9"/>
  </svg>
);

const IconBook = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
  </svg>
);

const IconClock = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);

const IconDownload = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const IconEye = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const IconPrinter = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 6 2 18 2 18 9"/>
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
    <rect x="6" y="14" width="12" height="8"/>
  </svg>
);

const IconSearch = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/>
    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const IconClose = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const IconFolder = () => (
  <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
  </svg>
);

const IconExternalLink = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
    <polyline points="15 3 21 3 21 9"/>
    <line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
);

const IconChevronLeft = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);

const IconChevronRight = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

const IconShieldCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    <polyline points="9 12 11 14 15 10"/>
  </svg>
);

function DocumentsUtilesPage() {
  useSEO({
    title: "Documents Utiles & Fournitures Scolaires (29 Classes) | Listes & Manuels Officiels",
    description: "Consultez et téléchargez les listes officielles des fournitures scolaires (CPEG, CPET, CPFPA) et manuels du GSU Les Palmiers à Togba (Abomey-Calavi). 29 classes disponibles en PDF.",
    keywords: "fournitures scolaires Togba, liste manuels scolaires Abomey-Calavi, fournitures CPEG CPET 2025-2026, documents officiels GSU Palmiers, téléchargement liste fournitures Bénin",
    canonicalUrl: "https://gsulespalmiers.bj/documents-utiles"
  });

  const [activeTab, setActiveTab] = useState('fournitures'); // 'fournitures' ou 'horaires'
  const [selectedCycle, setSelectedCycle] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  const [fournitures, setFournitures] = useState(defaultFournitures);
  const [schedules, setSchedules] = useState(defaultSchedules);
  const [loading, setLoading] = useState(false);

  // Modals state
  const [selectedFourniture, setSelectedFourniture] = useState(null);
  const [selectedSchedule, setSelectedSchedule] = useState(null);

  const loadData = React.useCallback(async () => {
    try {
      const [fournituresRes, schedulesRes] = await Promise.all([
        fournitureService.getAll().catch(() => null),
        fournitureService.getPublicSchedules().catch(() => null)
      ]);

      const rawFournitures = fournituresRes?.data?.data || (Array.isArray(fournituresRes?.data) ? fournituresRes.data : null);
      if (Array.isArray(rawFournitures) && rawFournitures.length > 0) {
        setFournitures(rawFournitures);
      } else {
        setFournitures(defaultFournitures);
      }

      const rawSchedules = schedulesRes?.data?.data || (Array.isArray(schedulesRes?.data) ? schedulesRes.data : null);
      if (Array.isArray(rawSchedules) && rawSchedules.length > 0) {
        setSchedules(rawSchedules);
      } else {
        setSchedules(defaultSchedules);
      }
    } catch (error) {
      console.error('Erreur chargement documents:', error);
      setFournitures(defaultFournitures);
      setSchedules(defaultSchedules);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (selectedFourniture || selectedSchedule) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setSelectedFourniture(null);
        setSelectedSchedule(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedFourniture, selectedSchedule]);

  const handleDownloadFourniture = async (item) => {
    if (!item) return;
    try {
      await fournitureService.incrementTelechargement(item.id);
    } catch (e) {
      // Ignorer l'erreur d'incrémentation
    }

    if (item.fichier_pdf) {
      const link = document.createElement('a');
      link.href = item.fichier_pdf;
      link.target = '_blank';
      link.download = `${item.titre || 'Fournitures'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }
  };

  const handlePrintSchedule = (scheduleItem) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Veuillez autoriser les fenêtres pop-up pour imprimer l\'emploi du temps.');
      return;
    }

    const coursList = scheduleItem.cours || [];
    const jours = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];

    let rowsHtml = '';
    jours.forEach(j => {
      const coursDuJour = coursList.filter(c => c.jour && c.jour.toLowerCase() === j);
      if (coursDuJour.length > 0) {
        rowsHtml += `
          <tr style="background: #eff6ff; font-weight: bold;">
            <td colspan="4" style="padding: 10px; color: #1e3a8a; text-transform: uppercase;">${j}</td>
          </tr>
        `;
        coursDuJour.forEach(c => {
          rowsHtml += `
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #e2e8f0;">${c.heure_debut || ''} - ${c.heure_fin || ''}</td>
              <td style="padding: 8px; border-bottom: 1px solid #e2e8f0; font-weight: 600;">${c.matiere || ''}</td>
              <td style="padding: 8px; border-bottom: 1px solid #e2e8f0;">${c.enseignant ? (c.enseignant.prenom + ' ' + c.enseignant.nom) : 'Enseignant GSU'}</td>
              <td style="padding: 8px; border-bottom: 1px solid #e2e8f0; color: #64748b;">${c.salle || 'Salle de classe'}</td>
            </tr>
          `;
        });
      }
    });

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Emploi du temps - ${scheduleItem.classe || ''} - GSU Les Palmiers</title>
        <style>
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 40px; color: #1e293b; line-height: 1.5; }
          .header { text-align: center; border-bottom: 2px solid #1e3a8a; padding-bottom: 15px; margin-bottom: 20px; }
          h1 { color: #1e3a8a; font-size: 22px; margin: 0; }
          .subtitle { color: #ff6b35; font-size: 15px; font-weight: bold; margin-top: 5px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #1e3a8a; color: white; padding: 10px; text-align: left; }
          .footer { text-align: center; margin-top: 40px; font-size: 12px; color: #64748b; border-top: 1px solid #e2e8f0; padding-top: 15px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>GROUPE SCOLAIRE ET UNIVERSITAIRE LES PALMIERS</h1>
          <div class="subtitle">EMPLOI DU TEMPS OFFICIEL • CLASSE DE ${(scheduleItem.classe || '').toUpperCase()} (${scheduleItem.annee_scolaire || '2026-2027'})</div>
        </div>
        <table>
          <thead>
            <tr>
              <th>Horaires</th>
              <th>Matière</th>
              <th>Enseignant</th>
              <th>Salle</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml || '<tr><td colspan="4" style="text-align:center; padding: 20px;">Aucun cours planifié.</td></tr>'}
          </tbody>
        </table>
        <div class="footer">
          Document officiel GSU Les Palmiers • Togba, Abomey-Calavi, Bénin • contact@gsulespalmiers.bj • info@gsulespalmiers.bj
        </div>
        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Normalisation
  const normalizeText = (text) => {
    return (text || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  };

  // Helper extraction des articles
  const getArticlesList = (item) => {
    if (!item || !item.articles) return [];
    if (Array.isArray(item.articles)) {
      return item.articles.filter(a => a && typeof a === 'string' && a.trim() !== '');
    }
    if (typeof item.articles === 'string') {
      try {
        const parsed = JSON.parse(item.articles);
        if (Array.isArray(parsed)) {
          return parsed.filter(a => a && typeof a === 'string' && a.trim() !== '');
        }
      } catch (e) {
        return item.articles.split('\n').map(a => a.trim()).filter(Boolean);
      }
    }
    return [];
  };

  // Détection intelligente du cycle de la fourniture
  const detectCycle = (f) => {
    if (!f) return 'college';
    const c = normalizeText(f.cycle);
    const cl = normalizeText(f.classe);
    const t = normalizeText(f.titre);

    if (c === 'college' || cl.includes('6e') || cl.includes('5e') || cl.includes('4e') || cl.includes('3e')) {
      return 'college';
    }
    if (c === 'seconde' || cl.includes('seconde') || cl.includes('2nde') || cl.includes('2nd') || t.includes('seconde')) {
      return 'seconde';
    }
    if (c === 'premiere' || cl.includes('premiere') || cl.includes('1ere') || cl.includes('1re') || t.includes('premiere')) {
      return 'premiere';
    }
    if (c === 'terminale' || cl.includes('terminale') || cl.includes('tle') || t.includes('terminale')) {
      return 'terminale';
    }
    if (c === 'superieur' || c.includes('superieur') || cl.includes('dwm') || cl.includes('bts') || cl.includes('licence') || t.includes('superieur') || t.includes('bts')) {
      return 'superieur';
    }
    return c || 'college';
  };

  // Filtrage des Fournitures par cycle et recherche
  const filteredFournitures = (fournitures || []).filter(f => {
    if (!f) return false;
    const fDetectedCycle = detectCycle(f);
    const fClasse = normalizeText(f.classe);
    const fTitre = normalizeText(f.titre);
    const query = normalizeText(searchQuery);

    let matchFilter = true;
    if (selectedCycle !== 'all') {
      matchFilter = fDetectedCycle === selectedCycle;
    }

    const matchSearch = query === '' || 
      fClasse.includes(query) || 
      fTitre.includes(query) || 
      (f.description && normalizeText(f.description).includes(query));

    return matchFilter && matchSearch;
  });

  // Filtrage des Emplois du Temps
  const filteredSchedules = (schedules || []).filter(s => {
    if (!s) return false;
    const sClasse = normalizeText(s.classe);
    const query = normalizeText(searchQuery);
    return query === '' || sClasse.includes(query);
  });

  // Navigation dans la modale d'aperçu
  const currentFournitureIndex = selectedFourniture 
    ? filteredFournitures.findIndex(f => f.id === selectedFourniture.id)
    : -1;
  const hasPrevFourniture = currentFournitureIndex > 0;
  const hasNextFourniture = currentFournitureIndex >= 0 && currentFournitureIndex < filteredFournitures.length - 1;

  const handlePrevFourniture = () => {
    if (hasPrevFourniture) {
      setSelectedFourniture(filteredFournitures[currentFournitureIndex - 1]);
    }
  };

  const handleNextFourniture = () => {
    if (hasNextFourniture) {
      setSelectedFourniture(filteredFournitures[currentFournitureIndex + 1]);
    }
  };

  return (
    <div className="documents-utiles-page">
      <Header activePage="documents-utiles" />

      {/* Hero Section */}
      <section className="documents-hero">
        <div className="hero-background"></div>
        <div className="container">
          <div className="hero-content">
            <span className="hero-badge">
              <IconDocument />
              <span>Espace Téléchargements & Rentrée</span>
            </span>
            <h1 className="hero-title">
              Fournitures & <span className="highlight">Emplois du Temps</span>
            </h1>
            <p className="hero-description">
              Consultez et téléchargez librement les listes officielles de fournitures, les manuels scolaires et les plannings de cours pour l'année scolaire 2026-2027.
            </p>
          </div>
        </div>
      </section>

      {/* Main Tabs Section */}
      <section className="documents-container section">
        <div className="container">
          
          <div className="main-tabs-nav">
            <button 
              className={`tab-btn-main ${activeTab === 'fournitures' ? 'active' : ''}`}
              onClick={() => { setActiveTab('fournitures'); setSelectedCycle('all'); }}
            >
              <IconBook />
              <span>Listes des Fournitures ({filteredFournitures.length})</span>
            </button>
            <button 
              className={`tab-btn-main ${activeTab === 'horaires' ? 'active' : ''}`}
              onClick={() => { setActiveTab('horaires'); setSelectedCycle('all'); }}
            >
              <IconClock />
              <span>Emplois du Temps ({filteredSchedules.length})</span>
            </button>
          </div>

          {/* Controls Bar: Cycle Filters & Search */}
          <div className="controls-bar">
            {activeTab === 'fournitures' && (
              <div className="cycle-filters">
                <button 
                  className={`cycle-btn ${selectedCycle === 'all' ? 'active' : ''}`}
                  onClick={() => setSelectedCycle('all')}
                >
                  Toutes ({fournitures.length})
                </button>
                <button 
                  className={`cycle-btn ${selectedCycle === 'college' ? 'active' : ''}`}
                  onClick={() => setSelectedCycle('college')}
                >
                  Collège ({fournitures.filter(f => detectCycle(f) === 'college').length})
                </button>
                <button 
                  className={`cycle-btn ${selectedCycle === 'seconde' ? 'active' : ''}`}
                  onClick={() => setSelectedCycle('seconde')}
                >
                  Secondes ({fournitures.filter(f => detectCycle(f) === 'seconde').length})
                </button>
                <button 
                  className={`cycle-btn ${selectedCycle === 'premiere' ? 'active' : ''}`}
                  onClick={() => setSelectedCycle('premiere')}
                >
                  Premières ({fournitures.filter(f => detectCycle(f) === 'premiere').length})
                </button>
                <button 
                  className={`cycle-btn ${selectedCycle === 'terminale' ? 'active' : ''}`}
                  onClick={() => setSelectedCycle('terminale')}
                >
                  Terminales ({fournitures.filter(f => detectCycle(f) === 'terminale').length})
                </button>
                <button 
                  className={`cycle-btn ${selectedCycle === 'superieur' ? 'active' : ''}`}
                  onClick={() => setSelectedCycle('superieur')}
                >
                  Supérieur ({fournitures.filter(f => detectCycle(f) === 'superieur').length})
                </button>
              </div>
            )}

            <div className="search-input-wrapper">
              <span className="search-icon">
                <IconSearch />
              </span>
              <input 
                type="text"
                placeholder="Rechercher une filière ou classe (ex: F3, G2, 6ème, HR, DWM, MMV)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button className="clear-search" onClick={() => setSearchQuery('')}>
                  <IconClose />
                </button>
              )}
            </div>
          </div>

          {/* TAB 1: FOURNITURES */}
          {activeTab === 'fournitures' && (
            <div className="documents-grid">
              {loading ? (
                <div className="loading-state">Chargement des listes de fournitures...</div>
              ) : filteredFournitures.length === 0 ? (
                <div className="no-data-state">
                  <div className="empty-icon-wrapper">
                    <IconFolder />
                  </div>
                  <h3>Aucune liste trouvée</h3>
                  <p>Aucun document ne correspond à vos critères de recherche.</p>
                </div>
              ) : (
                filteredFournitures.map((item) => (
                  <div key={item.id} className="doc-card">
                    <div className="doc-card-header">
                      <span className={`cycle-tag ${(detectCycle(item) || 'college').toLowerCase()}`}>
                        {item.cycle || 'Général'}
                      </span>
                      <span className="year-tag">{item.annee_scolaire || '2026-2027'}</span>
                    </div>

                    <div className="doc-card-body">
                      <div className="class-badge-circle">
                        <span>{(item.classe || '').substring(0, 5)}</span>
                      </div>
                      <h3 className="doc-title">{item.titre || 'Liste des Fournitures'}</h3>
                      <p className="doc-desc">{item.description || 'Liste officielle certifiée des fournitures et manuels scolaires.'}</p>
                      
                      <div className="doc-meta-info">
                        {getArticlesList(item).length > 0 && (
                          <span className="meta-item meta-articles">
                            <IconBook />
                            <span>{getArticlesList(item).length} articles listés</span>
                          </span>
                        )}
                        <span className="meta-item meta-pdf">
                          <IconDocument />
                          <span>PDF Officiel GSU</span>
                        </span>
                        {item.telechargements > 0 && (
                          <span className="meta-item meta-downloads">
                            <IconDownload />
                            <span>{item.telechargements} téléchargements</span>
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="doc-card-actions">
                      <button 
                        className="btn-preview"
                        onClick={() => setSelectedFourniture(item)}
                      >
                        <IconEye />
                        <span>Aperçu PDF</span>
                      </button>
                      <button 
                        className="btn-download"
                        onClick={() => handleDownloadFourniture(item)}
                      >
                        <IconDownload />
                        <span>Télécharger PDF</span>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* TAB 2: EMPLOIS DU TEMPS */}
          {activeTab === 'horaires' && (
            <div className="documents-grid">
              {loading ? (
                <div className="loading-state">Chargement des emplois du temps...</div>
              ) : filteredSchedules.length === 0 ? (
                <div className="no-data-state" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '4rem 1.5rem', background: '#ffffff', borderRadius: 20, boxShadow: '0 4px 20px rgba(0,0,0,0.04)', margin: '1rem 0' }}>
                  <div style={{ margin: '0 auto 1.25rem', width: 64, height: 64, borderRadius: '50%', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2563eb' }}>
                    <IconClock size={32} />
                  </div>
                  <h3 style={{ fontSize: '1.3rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.5rem' }}>Aucun emploi du temps disponible</h3>
                  <p style={{ color: '#64748b', fontSize: '0.95rem', maxWidth: 460, margin: '0 auto' }}>
                    Les plannings hebdomadaires officiels des cours et travaux pratiques du lycée seront publiés ici très prochainement.
                  </p>
                </div>
              ) : (
                filteredSchedules.map((schedule, idx) => (
                  <div key={idx} className="doc-card schedule-card">
                    <div className="doc-card-header">
                      <span className="cycle-tag college">Classe Officielle</span>
                      <span className="year-tag">{schedule.annee_scolaire}</span>
                    </div>

                    <div className="doc-card-body">
                      <div className="class-badge-circle schedule-badge">
                        <IconClock size={24} />
                      </div>
                      <h3 className="doc-title">Emploi du temps - {schedule.classe}</h3>
                      <p className="doc-desc">Planning hebdomadaire officiel des cours, travaux pratiques et devoirs surveillés.</p>
                      
                      <div className="doc-meta-info">
                        <span className="meta-item">
                          <IconBook />
                          <span>{schedule.total_cours || 0} créneaux / semaine</span>
                        </span>
                      </div>
                    </div>

                    <div className="doc-card-actions">
                      <button 
                        className="btn-preview"
                        onClick={() => setSelectedSchedule(schedule)}
                      >
                        <IconEye />
                        <span>Consulter</span>
                      </button>
                      <button 
                        className="btn-download"
                        onClick={() => handlePrintSchedule(schedule)}
                      >
                        <IconPrinter />
                        <span>Imprimer / PDF</span>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

        </div>
      </section>

      {/* MODALE APERÇU ULTRA-MODERNE FOURNITURE */}
      {selectedFourniture && (
        <div className="modal-overlay" onClick={() => setSelectedFourniture(null)}>
          <div className="modal-container preview-modal-ultra" onClick={(e) => e.stopPropagation()}>
            
            {/* Header Stylisé */}
            <div className="preview-ultra-header">
              <div className="header-left-meta">
                <div className="header-tags">
                  <span className="ultra-tag-cycle">{selectedFourniture.cycle}</span>
                  <span className="ultra-tag-class">{selectedFourniture.classe}</span>
                  <span className="ultra-tag-verified">
                    <IconShieldCheck />
                    <span>Certifié GSU</span>
                  </span>
                </div>
                <h2 className="ultra-title">{selectedFourniture.titre}</h2>
              </div>

              {/* Navigation rapide entre documents */}
              <div className="header-right-actions">
                <div className="doc-pagination-nav">
                  <button 
                    className="btn-nav-arrow" 
                    disabled={!hasPrevFourniture} 
                    onClick={handlePrevFourniture}
                    title="Document précédent"
                  >
                    <IconChevronLeft />
                  </button>
                  <span className="nav-counter">
                    {currentFournitureIndex + 1} / {filteredFournitures.length}
                  </span>
                  <button 
                    className="btn-nav-arrow" 
                    disabled={!hasNextFourniture} 
                    onClick={handleNextFourniture}
                    title="Document suivant"
                  >
                    <IconChevronRight />
                  </button>
                </div>

                <button className="ultra-close-btn" onClick={() => setSelectedFourniture(null)} title="Fermer">
                  <IconClose />
                </button>
              </div>
            </div>

            {/* Barre d'outils de visualisation */}
            <div className="preview-toolbar">
              <div className="toolbar-info">
                <span className="toolbar-dot"></span>
                <span>Document Officiel GSU Les Palmiers • Année Scolaire 2026-2027</span>
              </div>
              <div className="toolbar-buttons">
                {selectedFourniture.fichier_pdf && (
                  <a 
                    href={selectedFourniture.fichier_pdf} 
                    target="_blank" 
                    rel="noreferrer"
                    className="toolbar-btn"
                    title="Ouvrir dans un nouvel onglet"
                  >
                    <IconExternalLink />
                    <span>Plein écran</span>
                  </a>
                )}
                <button 
                  className="toolbar-btn" 
                  onClick={() => window.print()}
                  title="Imprimer directement"
                >
                  <IconPrinter />
                  <span>Imprimer</span>
                </button>
              </div>
            </div>

            {/* Corps de la modale avec la liste des articles et/ou le lecteur PDF */}
            <div className="preview-ultra-body">
              {/* Bouton d'ouverture directe PDF sur mobile */}
              {selectedFourniture.fichier_pdf && (
                <div className="mobile-pdf-direct-bar">
                  <a 
                    href={selectedFourniture.fichier_pdf} 
                    target="_blank" 
                    rel="noreferrer"
                    className="btn-mobile-open-pdf"
                  >
                    <IconExternalLink />
                    <span>Consulter / Ouvrir le PDF officiel en plein écran</span>
                  </a>
                </div>
              )}

              {/* Message de description */}
              {selectedFourniture.description && (
                <div className="modal-info-box">
                  <p style={{ margin: 0, fontWeight: 500 }}>{selectedFourniture.description}</p>
                </div>
              )}

              {/* Liste détaillée des fournitures et manuels */}
              {getArticlesList(selectedFourniture).length > 0 && (
                <div className="articles-section">
                  <h3 className="list-heading">
                    📋 Liste détaillée des fournitures & manuels ({getArticlesList(selectedFourniture).length} articles) :
                  </h3>
                  <ul className="articles-checklist">
                    {getArticlesList(selectedFourniture).map((art, idx) => (
                      <li key={idx} className="article-item">
                        <span className="check-bullet">✓</span>
                        <span>{art}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Document PDF si présent */}
              {selectedFourniture.fichier_pdf ? (
                <div className="pdf-frame-container">
                  {getArticlesList(selectedFourniture).length > 0 && (
                    <div style={{ padding: '0.75rem 1.5rem', background: '#f8fafc', borderTop: '1px solid #e2e8f0', color: '#1e3a8a', fontWeight: 600, fontSize: '0.95rem' }}>
                      📄 Télécharger ou consulter le document PDF officiel ci-dessous :
                    </div>
                  )}
                  <iframe 
                    src={`${selectedFourniture.fichier_pdf}#view=FitH&toolbar=1`}
                    title={selectedFourniture.titre}
                    className="pdf-iframe-view"
                  />
                </div>
              ) : (
                getArticlesList(selectedFourniture).length === 0 && (
                  <div className="no-pdf-notice">
                    <p>Document officiel disponible auprès du secrétariat du lycée GSU Les Palmiers.</p>
                  </div>
                )
              )}
            </div>

            {/* Footer Haute-Fidélité */}
            <div className="preview-ultra-footer">
              <div className="footer-meta">
                <span>Groupe Scolaire & Universitaire Les Palmiers • Cotonou</span>
              </div>
              <div className="footer-cta-group">
                <button className="btn btn-secondary" onClick={() => setSelectedFourniture(null)}>
                  Fermer
                </button>
                <button 
                  className="btn btn-primary btn-download-pulse" 
                  onClick={() => handleDownloadFourniture(selectedFourniture)}
                >
                  <IconDownload />
                  <span>Télécharger le PDF</span>
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* MODALE APERÇU EMPLOI DU TEMPS */}
      {selectedSchedule && (
        <div className="modal-overlay" onClick={() => setSelectedSchedule(null)}>
          <div className="modal-container schedule-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <span className="badge-tag">Année {selectedSchedule.annee_scolaire}</span>
                <h2>Planning Hebdomadaire : {selectedSchedule.classe}</h2>
              </div>
              <button className="modal-close" onClick={() => setSelectedSchedule(null)}>
                <IconClose />
              </button>
            </div>
            <div className="modal-body">
              <div className="schedule-table-wrapper">
                <table className="public-schedule-table">
                  <thead>
                    <tr>
                      <th>Jour</th>
                      <th>Horaires</th>
                      <th>Matière</th>
                      <th>Enseignant</th>
                      <th>Salle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedSchedule.cours && selectedSchedule.cours.map((c, i) => (
                      <tr key={i}>
                        <td className="day-col">{toSentenceCase(c.jour)}</td>
                        <td className="time-col">{c.heure_debut} - {c.heure_fin}</td>
                        <td className="subject-col"><strong>{c.matiere}</strong></td>
                        <td className="teacher-col">{c.enseignant ? `${c.enseignant.prenom} ${c.enseignant.nom}` : 'Professeur principal'}</td>
                        <td className="room-col"><span className="room-badge">{c.salle || 'Salle Standard'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setSelectedSchedule(null)}>Fermer</button>
              <button className="btn btn-primary" onClick={() => handlePrintSchedule(selectedSchedule)}>
                <IconPrinter />
                <span>Imprimer / Télécharger</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default DocumentsUtilesPage;
